import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { MarkdownContent } from "@9amhealth/shared";
import HtmlParser from "src/lib/HtmlParser";
import {
  CheckmarkMessageDelivered,
  CheckmarkMessageSent
} from "src/constants/icons";
import AvatarCubit from "src/state/AvatarCubit/AvatarCubit";
import { useBloc } from "src/state/state";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { useBlocNext } from "@blac/react";
import type { ChatMessage } from "../Chat/ChatBloc";
import { ChatBloc } from "../Chat/ChatBloc";
import { formatMessageTime } from "./state/utils";
import MessageFiles from "./MessageFiles";
import type { LeapChatFileRef } from "./state/types";
import { AppActionButton } from "../AppActionButton/AppActionButton";
import {
  AssistantAvatar,
  AssistantMessageBody,
  AssistantMessageContent,
  AssistantMessageRoot,
  ComposerCardArea,
  ComposerRow,
  FooterName,
  HeroInput,
  HeroInputCard,
  HeroInputCardWrapper,
  HtmlContentWrapper,
  LeapChatContainer,
  LeapChatViewport,
  MessageFooter,
  MessageListContent,
  SendButton,
  TextLink,
  UserMessageContent,
  UserMessageRoot
} from "./leapChat.styles";
import { CHAT_PAGINATION } from "../Chat/constants/ChatConstants";
import { METADATA_CONTENT_VALUE } from "src/constants/misc";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import translate from "src/lib/translate";

const ArrowUpIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="15"
    fill="none"
    viewBox="0 0 13 15"
  >
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M6.083 14.083V.75m0 0L.75 6.083M6.083.75l5.334 5.333"
    />
  </svg>
);

const LegacyParsedHtmlContent: FC<{ text: string }> = memo(({ text }) => {
  const parsed = useMemo(
    () => new HtmlParser(text, { convertMarkdownToHtml: true }).toJsx(),
    [text]
  );
  return <HtmlContentWrapper>{parsed}</HtmlContentWrapper>;
});

const toFileRefs = (
  attachments: ChatMessage["computed"]["fileAttachments"]
): LeapChatFileRef[] =>
  attachments.map((a) => ({
    fileId: a.source,
    name: a.name ?? null,
    type: a.type ?? null,
    size: a.size ? Number(a.size) : null
  }));

const LegacyUserBubble: FC<{ msg: ChatMessage }> = memo(({ msg }) => (
  <UserMessageRoot>
    <UserMessageContent>
      {msg.computed.fileAttachments.length > 0 && (
        <MessageFiles files={toFileRefs(msg.computed.fileAttachments)} />
      )}
      {msg.text &&
        (msg.type === "text/html" ? (
          <LegacyParsedHtmlContent text={msg.text} />
        ) : (
          <MarkdownContent text={msg.text} />
        ))}
    </UserMessageContent>
    <MessageFooter className="user-footer">
      {formatMessageTime(new Date(msg.timestamp).getTime())}
      <span className="confirmation">
        {msg.computed.isLocal ? (
          <CheckmarkMessageSent />
        ) : (
          <CheckmarkMessageDelivered />
        )}
      </span>
    </MessageFooter>
  </UserMessageRoot>
));

const LegacyAssistantBubble: FC<{ msg: ChatMessage }> = memo(({ msg }) => {
  const [avatar] = useBloc(AvatarCubit, {
    create: () => new AvatarCubit(msg.apiItem?.senderId ?? "")
  });
  const avatarUrl = avatar.path ?? "/9amAvatar.png";

  return (
    <AssistantMessageRoot>
      <AssistantMessageBody>
        <AssistantMessageContent>
          {msg.computed.fileAttachments.length > 0 && (
            <MessageFiles files={toFileRefs(msg.computed.fileAttachments)} />
          )}
          {msg.text &&
            (msg.type === "text/html" ? (
              <LegacyParsedHtmlContent text={msg.text} />
            ) : (
              <MarkdownContent text={msg.text} />
            ))}
        </AssistantMessageContent>
        {msg.computed.senderName && (
          <MessageFooter>
            <AssistantAvatar>
              <img
                src={avatarUrl}
                alt={msg.computed.senderName ?? "9am.health"}
              />
            </AssistantAvatar>
            <FooterName>{msg.computed.senderName}</FooterName>
            {msg.computed.senderDescription
              ? ` · ${msg.computed.senderDescription}`
              : null}
            <span className="footer-time">
              {formatMessageTime(new Date(msg.timestamp).getTime())}
            </span>
          </MessageFooter>
        )}
      </AssistantMessageBody>
    </AssistantMessageRoot>
  );
});

const LegacyComposer: FC<{ chatBloc: ChatBloc }> = ({ chatBloc }) => {
  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return;
    }
    void chatBloc.postMessage(trimmed, null);
    setInputValue("");
  };

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!inputValue) {
      const el = textareaRef.current;
      if (el) {
        el.style.height = "auto";
      }
    }
  }, [inputValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const addFiles = (newFiles: FileList | null) => {
    if (newFiles && newFiles.length > 0) {
      const [onSuccess, onError] =
        BlockingLoadingOverlayController.startLoading({
          bg: "transparent",
          message: translate("notifications.uploading")
        });
      void chatBloc
        .postMessage("File upload", newFiles, {
          [METADATA_CONTENT_VALUE]: true
        })
        .then(() => onSuccess())
        .catch(() => onError());
    }
  };

  const canSend = Boolean(inputValue.trim());

  return (
    <ComposerRow>
      <AppActionButton addFiles={addFiles} />
      <HeroInputCardWrapper>
        <HeroInputCard>
          <HeroInput
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              autoResize();
            }}
            onKeyDown={handleKeyDown}
            placeholder={translate("leapChat.placeholder.messageYourCareTeam")}
            rows={1}
          />
          {canSend && (
            <SendButton
              type="button"
              onClick={handleSend}
              aria-label={translate("leapChat.aria.sendMessage")}
            >
              <ArrowUpIcon />
            </SendButton>
          )}
        </HeroInputCard>
      </HeroInputCardWrapper>
    </ComposerRow>
  );
};

const LegacyChatView: FC = () => {
  const [{ messages, loading }, chatBloc] = useBlocNext(ChatBloc);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(true);
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    chatBloc.initChat({ userId: StorageController.activeUserId ?? undefined });
    return () => {
      chatBloc.setChatOpen(false);
    };
  }, [chatBloc]);

  useEffect(() => {
    if (isLoadingMoreRef.current) {
      return;
    }
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    setHasMore(chatBloc.moreHistoryAvailable);
  }, [messages, chatBloc]);

  const visibleMessages = messages.filter((m) => m.placeholder === undefined);

  const showLoadMore =
    hasMore && visibleMessages.length >= CHAT_PAGINATION.MESSAGES_PER_PAGE;

  const handleLoadMore = useCallback(async () => {
    const scrollEl = scrollRef.current;
    const contentEl = contentRef.current;
    if (!scrollEl || !contentEl) {
      return;
    }

    const prevScrollTop = scrollEl.scrollTop;
    const prevContentHeight = contentEl.scrollHeight;

    isLoadingMoreRef.current = true;

    await chatBloc.loadPreviousMessages();
    setHasMore(chatBloc.moreHistoryAvailable);

    requestAnimationFrame(() => {
      const addedHeight = contentEl.scrollHeight - prevContentHeight;
      scrollEl.scrollTop = prevScrollTop + addedHeight;
      isLoadingMoreRef.current = false;
    });
  }, [chatBloc]);

  return (
    <LeapChatContainer>
      <LeapChatViewport ref={scrollRef}>
        <MessageListContent ref={contentRef}>
          {showLoadMore && (
            <div style={{ textAlign: "center", padding: "0.8rem 0" }}>
              <TextLink
                type="button"
                onClick={() => void handleLoadMore()}
                disabled={loading}
              >
                {loading
                  ? translate("leapChat.loading")
                  : translate("leapChat.loadEarlierMessages")}
              </TextLink>
            </div>
          )}
          {visibleMessages.map((msg) =>
            msg.computed.sentByUser ? (
              <LegacyUserBubble key={msg.id} msg={msg} />
            ) : (
              <LegacyAssistantBubble key={msg.id} msg={msg} />
            )
          )}
        </MessageListContent>
        <ComposerCardArea
          ref={composerRef}
          data-brioche-bottom
          className={"brioche-anchor-override"}
        >
          <LegacyComposer chatBloc={chatBloc} />
        </ComposerCardArea>
      </LeapChatViewport>
    </LeapChatContainer>
  );
};

export default LegacyChatView;
